/**
 * Login Page Component
 * User authentication login interface with form validation
 */

import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { useNotifications } from '../../context/NotificationContext';
import type { LoginCredentials } from '../../types/auth';
import './Login.css';

interface LoginFormErrors {
  emailOrUsername?: string;
  password?: string;
  general?: string;
}

const Login: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login, isAuthenticated, isLoading, error, clearError } = useAuth();
  const { showSuccess, showError } = useNotifications();

  // Form state
  const [formData, setFormData] = useState<LoginCredentials>({
    emailOrUsername: '',
    password: ''
  });

  const [formErrors, setFormErrors] = useState<LoginFormErrors>({});
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Redirect to intended page after login
  const from = (location.state as any)?.from?.pathname || '/dashboard';

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, from]);

  // Clear errors when component mounts or unmounts
  useEffect(() => {
    clearError();
    return () => clearError();
  }, [clearError]);

  // Handle form input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear field-specific error when user starts typing
    if (formErrors[name as keyof LoginFormErrors]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  // Validate form data
  const validateForm = (): boolean => {
    const errors: LoginFormErrors = {};

    // Email/Username validation
    if (!formData.emailOrUsername.trim()) {
      errors.emailOrUsername = 'Email or username is required';
    }

    // Password validation
    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      errors.password = 'Password must be at least 6 characters';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    clearError();

    try {
      const ok = await login(formData);
      if (ok) {
        showSuccess('Login Successful', 'Welcome back! Redirecting to dashboard...');
        // Navigation will be handled by useEffect when isAuthenticated changes
      } else {
        const errorMessage = error || 'Invalid credentials';
        showError('Login Failed', errorMessage);
        setFormErrors({ general: errorMessage });
      }
    } catch (err) {
      console.error('Login error:', err);
      const errorMessage = error || 'Login failed. Please try again.';
      showError('Login Failed', errorMessage);
      setFormErrors({ general: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Handle demo login
  const handleDemoLogin = async () => {
    // For now, show message that demo is not available
    setFormErrors({
      general: 'Demo login not available yet. Please register a new account or use your existing credentials.'
    });
  };

  if (isLoading) {
    return (
      <div className="login-container">
        <div className="login-card">
          <div className="login-loading">
            <div className="spinner"></div>
            <p>Checking authentication...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>Welcome Back</h1>
          <p>Sign in to your Kadi Ni Tidi account</p>
        </div>

        <form onSubmit={handleSubmit} className="login-form">
          {/* General Error Display */}
          {(error || formErrors.general) && (
            <div className="error-message">
              {error || formErrors.general}
            </div>
          )}

          {/* Email/Username Field */}
          <div className="form-group">
            <label htmlFor="emailOrUsername">Email or Username</label>
            <input
              type="text"
              id="emailOrUsername"
              name="emailOrUsername"
              value={formData.emailOrUsername}
              onChange={handleInputChange}
              placeholder="Enter your email or username"
              className={formErrors.emailOrUsername ? 'error' : ''}
              disabled={isSubmitting}
              autoComplete="username"
            />
            {formErrors.emailOrUsername && (
              <span className="field-error">{formErrors.emailOrUsername}</span>
            )}
          </div>

          {/* Password Field */}
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <div className="password-input-container">
              <input
                type={showPassword ? 'text' : 'password'}
                id="password"
                name="password"
                value={formData.password}
                onChange={handleInputChange}
                placeholder="Enter your password"
                className={formErrors.password ? 'error' : ''}
                disabled={isSubmitting}
                autoComplete="current-password"
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
                disabled={isSubmitting}
              >
                {showPassword ? '👁️' : '🙈'}
              </button>
            </div>
            {formErrors.password && (
              <span className="field-error">{formErrors.password}</span>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            className="login-button"
            disabled={isSubmitting}
          >
            {isSubmitting ? (
              <>
                <span className="spinner-small"></span>
                Signing In...
              </>
            ) : (
              'Sign In'
            )}
          </button>

          {/* Demo Login Button */}
          <button
            type="button"
            onClick={handleDemoLogin}
            className="demo-button"
            disabled={true}
            title="Demo account will be available soon"
          >
            🚧 Demo Account (Coming Soon)
          </button>
        </form>

        {/* Links */}
        <div className="login-links">
          <Link to="/forgot-password" className="forgot-password-link">
            Forgot your password?
          </Link>
        </div>

        <div className="signup-prompt">
          <p>
            Don't have an account?{' '}
            <Link to="/register" className="signup-link">
              Sign up here
            </Link>
          </p>
        </div>

        {/* Footer */}
        <div className="login-footer">
          <p>&copy; 2024 Kadi Ni Tidi Platform. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
};

export default Login;